import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Clock,
  Timer,
  CreditCard,
  Stethoscope,
  Shirt,
  PhoneOff,
  ArrowLeft,
  Heart,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };

const RULES = [
  {
    icon: Clock,
    title: "Időpont lemondása",
    accent: "#2A9D8F",
    items: [
      "Lemondás vagy módosítás legkésőbb 24 órával az időpont előtt lehetséges.",
      "24 órán belüli lemondás vagy meg nem jelenés esetén a kezelés díja felszámításra kerül.",
      "Ha beteg vagy, inkább egyeztessünk másik időpontot – mindannyiunk védelme érdekében.",
    ],
  },
  {
    icon: Timer,
    title: "Pontos érkezés",
    accent: "#5BB5A9",
    items: [
      "Kérünk, érkezz pontosan.",
      "15 perc késés esetén a masszázs időtartama a késés mértékével csökken.",
    ],
  },
  {
    icon: CreditCard,
    title: "Fizetés",
    accent: "#D4AF37",
    items: [
      "Fizetés készpénzben vagy átutalással lehetséges.",
      "Kérünk, lehetőség szerint az összeghez közeli címlettel készülj.",
    ],
  },
  {
    icon: Stethoscope,
    title: "Egészségügyi állapot",
    accent: "#A88A2B",
    items: [
      "A kezelés előtt rövid kikérdezés történik.",
      "Kérünk, tájékoztass minden fennálló betegségről, sérülésről, műtétről, várandósságról vagy gyógyszerszedésről.",
    ],
  },
  {
    icon: Shirt,
    title: "Öltözet",
    accent: "#2A9D8F",
    items: ["Kérünk, kényelmes ruhában érkezz."],
  },
  {
    icon: PhoneOff,
    title: "Nyugodt környezet",
    accent: "#5BB5A9",
    items: ["A masszázs ideje alatt kérünk, némítsd le telefonodat."],
  },
];

export default function Hazirend() {
  return (
    <div data-testid="hazirend-page" className="text-[#1A2825]">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-8">
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#A88A2B] hover:text-[#1C6B61] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Vissza a kezdőlapra
          </Link>
          <span className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#A88A2B]">
            <BookOpen className="h-3.5 w-3.5" /> Stúdió · Vendégeknek
          </span>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#1A2825] mt-4 leading-[1.05]">
            <em className="not-italic text-[#2A9D8F]">Házirend</em>
          </h1>
          <p className="mt-5 text-lg text-[#3A4D4A] font-light leading-relaxed">
            Kedves Vendégünk! A nyugodt, zavartalan kezelések érdekében kérünk, tartsd be az
            alábbiakat.
          </p>
        </motion.div>
      </section>

      {/* Rules */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          variants={stagger}
          className="grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          {RULES.map((r, idx) => {
            const Icon = r.icon;
            return (
              <motion.div
                key={r.title}
                variants={fadeUp}
                data-testid={`hazirend-rule-${idx}`}
                className="bg-white rounded-[1.75rem] border border-[#EAE5D9] p-6 md:p-7 flex flex-col shadow-[0_18px_50px_-30px_rgba(28,107,97,0.35)]"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="h-10 w-10 rounded-full flex items-center justify-center text-white shadow-sm"
                    style={{ backgroundColor: r.accent }}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <h2 className="font-serif-display text-xl md:text-2xl text-[#1A2825] leading-snug">
                    {r.title}
                  </h2>
                </div>
                <ul className="mt-4 space-y-2 text-[15px] text-[#3A4D4A] font-light leading-relaxed">
                  {r.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span
                        aria-hidden="true"
                        className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: r.accent }}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* Closing */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="inline-flex flex-col items-center gap-3"
        >
          <Heart className="h-5 w-5 text-[#D4AF37]" />
          <p className="font-serif-display text-2xl md:text-3xl text-[#1A2825] leading-snug max-w-xl">
            Köszönjük az együttműködésed!
          </p>
          <Link
            to="/adatkezeles"
            data-testid="hazirend-privacy-link"
            className="mt-4 text-sm text-[#758C88] hover:text-[#1C6B61] transition-colors underline underline-offset-2"
          >
            Adatkezelési tájékoztató
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
